import { useEffect, useRef } from 'react'

import type { ListCareLogsParams } from '@/lib/api/care-logs-api'
import { getAnalyticsActor, posthog } from '@/lib/posthog'

/**
 * Telemetry của màn danh sách nhật ký chăm sóc (FD-401). Chỉ gửi enum/boolean +
 * actor: KHÔNG gửi id/tên hộ, id/tên vùng, số dòng hay message lỗi. Contract:
 * `docs/analytics/care-log-list.md`. Analytics lỗi không được chặn thao tác.
 */
function capture(event: string, props: Record<string, unknown>) {
    const identity = getAnalyticsActor()
    if (!identity) return
    try {
        posthog.capture(event, { schema_version: 1, ...props, ...identity })
    } catch {
        // Analytics failure must not block the list.
    }
}

/** Bộ lọc nào đang bật lúc xuất — chỉ có/không, không kèm giá trị. */
function filterDims(params: ListCareLogsParams) {
    return {
        has_grower_filter: !!params.grower_id,
        has_area_filter: !!params.area_id,
        has_activity_filter: !!params.activity_type,
    }
}

/** Nhóm lỗi theo HTTP status; không có response (mạng/CORS) là `network`. */
function failureKind(err: unknown): 'client' | 'server' | 'network' {
    const status = (err as { response?: { status?: unknown } } | null)?.response
        ?.status
    if (typeof status !== 'number') return 'network'
    return status >= 500 ? 'server' : 'client'
}

/**
 * Mỗi lần bấm "Xuất Excel" kết thúc: tải được file (`downloaded`) hay không
 * (`failed` + `failure`). `client` gồm cả ca vượt trần 10.000 dòng — người dùng
 * cần thu hẹp bộ lọc.
 */
export function trackCareLogExport(
    params: ListCareLogsParams,
    outcome: { ok: true } | { ok: false; error: unknown },
) {
    capture('care_log_exported', {
        result: outcome.ok ? 'downloaded' : 'failed',
        ...(outcome.ok ? {} : { failure: failureKind(outcome.error) }),
        ...filterDims(params),
    })
}

/**
 * Người dùng có lọc theo vùng trồng không: phát một lần mỗi khi một vùng HỢP LỆ
 * (có trong danh sách của ô lọc) được áp dụng. Cùng vùng không phát lại; bỏ lọc
 * rồi chọn lại tính lượt mới. `?area=` lạ (sắp bị màn tự bỏ) không tính.
 */
export function useCareLogAreaFilterTracking({
    areaId,
    isKnownArea,
    isAdmin,
}: {
    areaId: string
    isKnownArea: boolean
    isAdmin: boolean
}) {
    const reportedFor = useRef<string | null>(null)

    useEffect(() => {
        if (!areaId) {
            reportedFor.current = null
            return
        }
        if (!isKnownArea || reportedFor.current === areaId) return
        reportedFor.current = areaId
        capture('care_log_area_filtered', {
            stage: 'applied',
            scope: isAdmin ? 'all_growers' : 'own_growers',
        })
    }, [areaId, isKnownArea, isAdmin])
}
